import { Item } from '../../services/list'
import itemOrder from '../../services/item-order'

// The list's own sequence, by the shared order (services/item-order — kept in
// step with the list row, so a drag on one device lands on all of them). Items
// not in it yet (freshly added, or added by a member since the last drag) keep
// server order (newest first) and lead, so new items surface at the top
// everywhere. Check state is deliberately not part of it: an item keeps its
// slot — and so its section — while it sits in the done zone.
export const sortByOrder = (listId: string, items: Item[]): Item[] => {
    const order = itemOrder.get(listId)
    const pos = new Map(order.map((id, i) => [id, i]))
    const known = items
        .filter(it => pos.has(it.id))
        .sort((a, b) => (pos.get(a.id) as number) - (pos.get(b.id) as number))
    const unknown = items.filter(it => !pos.has(it.id))
    return [...unknown, ...known]
}

// Display only: checked items settle into the "done" zone at the bottom, each
// group keeping its relative order. Unchecking simply drops this partition, so
// the item glides back to where it was ticked.
export const doneLast = (items: Item[]): Item[] =>
    [...items.filter(it => !it.checked), ...items.filter(it => it.checked)]

export const applyOrder = (listId: string, items: Item[]): Item[] =>
    doneLast(sortByOrder(listId, items))

// Fold a drag back into the shared sequence. Done rows are shown in the bottom
// zone rather than at their real slot, so they hold those slots and the dragged
// active rows fill the rest — a drag can't move a done item out of its section
// behind the user's back. `dragged` is the new on-screen order.
export const foldDrag = (listId: string, dragged: Item[]): string[] => {
    const active = dragged.filter(it => !it.checked).map(it => it.id)
    const done = new Set(dragged.filter(it => it.checked).map(it => it.id))
    // One slot per item, in the pre-drag sequence — so the counts always match.
    const slots = sortByOrder(listId, dragged).map(it => it.id)
    return slots.map(id => (done.has(id) ? id : active.shift() as string))
}
